import { useCallback, useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { SearchParam, SearchParamIntent, URLStateActions } from "../types";
import useNavigation from "./navigation";
import { useTeam } from "./useTeams";

export const TeamIntentErrors = {
  Unauthorized: "UNAUTHORIZED",
  TeamNotFound: "TEAM_NOT_FOUND",
  RenameSpaceUnauthorized: "RENAME_SPACE_UNAUTHORIZED",
} as const;
export type TeamIntentError = (typeof TeamIntentErrors)[keyof typeof TeamIntentErrors];

export const useTeamIntents = ({
  teamID,
}: {
  teamID: string | undefined;
}): {
  isLoading: boolean;
  error: TeamIntentError | null;
  clear: () => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigation = useNavigation();
  const { teamData, isOwner, isSeatManager, isLoading } = useTeam(teamID);
  const [error, setError] = useState<TeamIntentError | null>(null);
  const [intent, setIntent] = useState<SearchParamIntent | null>(null);

  const clear = useCallback(() => {
    setError(null);
    setIntent(null);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete(SearchParam.Intent);
      newParams.delete(SearchParam.SpaceID);
      return newParams;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (intent) {
      return;
    }
    const intentFromParam = searchParams.get(SearchParam.Intent);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(SearchParam.Intent);
    setSearchParams(newParams, { replace: true });
    setIntent(intentFromParam as SearchParamIntent);
  }, [searchParams, setSearchParams, intent]);

  useEffect(() => {
    if (!intent || isLoading) {
      return;
    }

    setIntent(null);

    if (!teamData) {
      setError(TeamIntentErrors.TeamNotFound);
      return;
    }

    if (intent === SearchParamIntent.RenameSpace) {
      if (!isOwner && !isSeatManager) {
        setError(TeamIntentErrors.RenameSpaceUnauthorized);
        return;
      }

      const spaceID = searchParams.get(SearchParam.SpaceID);
      if (!spaceID) {
        setError(TeamIntentErrors.RenameSpaceUnauthorized);
        return;
      }

      navigation.goToTeamSettings({
        teamID: teamData.id,
        omitQueryParams: [SearchParam.Intent, SearchParam.SpaceID],
        state: {
          Action: URLStateActions.OpenRenameSpaceModal,
          Params: {
            SpaceID: spaceID,
          },
        },
      });
      return;
    }

    if (intent === SearchParamIntent.Invite) {
      navigation.goToTeamMembers({
        teamID: teamData.id,
        omitQueryParams: [SearchParam.Intent],
        state: {
          Action: URLStateActions.OpenInviteModal,
        },
      });
      return;
    }

    clear();
  }, [intent, isLoading, teamData, isOwner, isSeatManager, navigation, searchParams, clear]);

  return {
    isLoading,
    error,
    clear,
  };
};
